import { useId, useRef, useState } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useDismiss } from '@/hooks/useDismiss'
import { TEXT_SIZES } from '@/contexts/ThemeContext'
import type { TextSize } from '@/contexts/ThemeContext'
import { cn } from '@/utils/cn'
import { filledFill, focusRing, iconFill, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { useIsPhone } from '@/hooks/useIsPhone'
import { SheetHandle, SheetWrapper, sheetDocked, sheetSafeArea } from '@/components/ui/Sheet'

const LABELS: Record<TextSize, string> = {
  small: 'Small',
  default: 'Default',
  large: 'Large',
  larger: 'Larger',
}

interface TextSizeControlProps {
  className?: string
}

/**
 * App-wide text-size step (small / default / large / larger). Sits beside the
 * theme toggle; a radiogroup in a small popover with arrow-key selection.
 */
export function TextSizeControl({ className }: TextSizeControlProps) {
  const { textSize, setTextSize } = useTheme()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const isPhone = useIsPhone()
  const groupRef = useRef<HTMLDivElement>(null)
  const labelId = useId()

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  useDismiss(wrapperRef, close, open, { alsoInside: sheetRef })
  useSheetAnchor(open, triggerRef, sheetRef)

  function moveSelection(delta: 1 | -1) {
    const next = TEXT_SIZES[TEXT_SIZES.indexOf(textSize) + delta]
    if (!next) return
    setTextSize(next)
    groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[TEXT_SIZES.indexOf(next)]?.focus()
  }

  function handleGroupKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1) }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveSelection(-1) }
  }

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-label="Text size"
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(iconFill, 'font-serif', open && filledFill)}
      >
        <span aria-hidden="true" className="flex items-baseline gap-px leading-none">
          <span className="text-sm">A</span>
          <span className="text-xl">A</span>
        </span>
      </button>

      {open && (
        <SheetWrapper isPhone={isPhone} onScrimClick={close}>
        <div
          role="dialog"
          aria-labelledby={labelId}
          data-testid={isPhone ? 'bottom-sheet' : undefined}
          ref={sheetRef}
          className={cn(
            'animate-fade-in',
            isPhone
              ? cn(sheetDocked, sheetSafeArea, 'px-5 pb-4')
              : 'absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border border-border-divider bg-card p-3 shadow-lg',
          )}
        >
          <SheetHandle />
          <span id={labelId} className="block px-1 font-mono text-xs uppercase tracking-widest text-muted">
            Text size
          </span>
          <div
            ref={groupRef}
            role="radiogroup"
            aria-labelledby={labelId}
            onKeyDown={handleGroupKey}
            className="mt-2 grid grid-cols-2 gap-1"
          >
            {TEXT_SIZES.map((size) => {
              const checked = size === textSize
              return (
                <button
                  key={size}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  onClick={() => setTextSize(size)}
                  className={cn(
                    'min-h-11 rounded-md text-sm font-normal transition-colors duration-200',
                    focusRing,
                    checked ? segmentedSelected : segmentedRest,
                  )}
                >
                  {LABELS[size]}
                </button>
              )
            })}
          </div>
        </div>
        </SheetWrapper>
      )}
    </div>
  )
}
